/**
 * CooldownRing — visual feedback for the tap cooldown introduced 2026-05-24.
 *
 * Fills clockwise from the top over `state.cooldownMs` from the moment
 * of the last tap, then stays full and softly breathes until the next
 * tap.
 *
 * Implementation:
 *   - SVG circle with stroke-dasharray = circumference, dashoffset
 *     animated each frame from circumference (empty) to 0 (full).
 *   - requestAnimationFrame loop only runs while charging; when
 *     progress reaches 1 the loop stops (no idle CPU drain).
 *   - When tap is rejected (lastTapAt unchanged), the ring stays where
 *     it was — no shake, no flash. Per JB "silently ignored".
 *   - Doc 28 §B (cycles audit) — on accepted tap, the whole ring
 *     scale-bounces 1 → 1.035 → 1 over 280ms. Tactile feedback что
 *     «тап сработал», без шума и звонкости. framer-motion controls.
 *
 * Future:
 *   - Multiple animation styles (clockwise / counter-clockwise / inside-
 *     out / dot-orbit), randomly picked per tap. The CSS module is
 *     structured so we can add `.styleClockwise`, `.styleCounter`,
 *     etc. and rotate via React state when JB approves designs.
 */
import { motion, useAnimationControls } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useGameStore } from '../stores/game.js';
import styles from './CooldownRing.module.css';

const SIZE = 230; // matches Spark width (200px) + ~14px inset
const STROKE = 4;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function CooldownRing(): JSX.Element {
  const cooldownMs = useGameStore((s) => s.state.cooldownMs);
  const lastTapAt = useGameStore((s) => s.state.stats.lastTapAt);

  // Progress 0..1. Starts at 1 (ready) before any tap.
  const [progress, setProgress] = useState<number>(1);

  // Doc 28 §B — scale-bounce on accepted tap. Animation controls
  // позволяют ре-trigger одну и ту же анимацию каждый раз когда
  // lastTapAt меняется, без remount компонента.
  const bounceControls = useAnimationControls();

  useEffect(() => {
    if (lastTapAt === null) {
      setProgress(1);
      return;
    }
    let rafId: number | null = null;
    const tick = (): void => {
      const elapsed = Date.now() - lastTapAt;
      const pct = Math.min(1, elapsed / cooldownMs);
      setProgress(pct);
      if (pct < 1) {
        rafId = window.requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    };
    rafId = window.requestAnimationFrame(tick);
    // Fire bounce on accepted tap (lastTapAt только меняется когда тап
    // принят — отклонённые тапы оставляют lastTapAt прежним).
    void bounceControls.start({
      scale: [1, 1.035, 1],
      transition: { duration: 0.28, ease: 'easeOut' },
    });
    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [lastTapAt, cooldownMs, bounceControls]);

  const ready = progress >= 1;
  const offset = useMemo(() => CIRCUMFERENCE * (1 - progress), [progress]);
  const progressClass = ready
    ? `${styles.progress ?? ''} ${styles.progressReady ?? ''}`
    : (styles.progress ?? '');

  return (
    <motion.svg
      animate={bounceControls}
      className={styles.ring}
      width={SIZE}
      height={SIZE}
      viewBox={`0 0 ${SIZE.toString()} ${SIZE.toString()}`}
      aria-hidden
      style={{ originX: '50%', originY: '50%' }}
    >
      {/* Faint always-on track so the ring outline is visible even when
          fully filled — helps the eye localize where the next tap will
          reset the fill. */}
      <circle
        className={styles.track}
        cx={SIZE / 2}
        cy={SIZE / 2}
        r={RADIUS}
        strokeWidth={STROKE - 1}
      />
      {/* The moving gold arc. */}
      <circle
        className={progressClass}
        cx={SIZE / 2}
        cy={SIZE / 2}
        r={RADIUS}
        strokeDasharray={`${CIRCUMFERENCE.toString()} ${CIRCUMFERENCE.toString()}`}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${(SIZE / 2).toString()} ${(SIZE / 2).toString()})`}
      />
    </motion.svg>
  );
}
